import {
  Box,
  BoxProps,
  ElementProps,
  factory,
  Factory,
  StylesApiProps,
  Text,
  Title,
  useProps,
  useStyles,
} from "@mantine/core";
import type { ReactNode } from "react";

import classes from "./FullPageMessage.module.css";

export type FullPageMessageStylesNames =
  | "root"
  | "hero"
  | "title"
  | "body"
  | "action";

export interface FullPageMessageProps
  extends BoxProps,
    StylesApiProps<FullPageMessageFactory>,
    // `title` is omitted because a `div`'s own `title` attribute would collide with the
    // heading prop below — and a tooltip is not what a caller means here
    ElementProps<"div", "title"> {
  /**
   * An optional image above the heading. A node rather than a `src`, so the caller owns its
   * dimensions and its alt text — a logo and an illustration want different sizes, and only
   * the caller knows whether the image is decorative.
   */
  hero?: ReactNode;
  /** The heading. Rendered as the page's `h1` */
  title: string;
  /** One line explaining why there is nothing to show */
  body: string;
  /**
   * The way out. Required, because every screen of this kind is a dead end and one with no
   * recovery leaves the user only the Back button. A node rather than a label and a
   * handler, so a caller can pass two controls or a link without a prop for each.
   */
  action: ReactNode;
}

export type FullPageMessageFactory = Factory<{
  props: FullPageMessageProps;
  ref: HTMLDivElement;
  stylesNames: FullPageMessageStylesNames;
}>;

const defaultProps = {} satisfies Partial<FullPageMessageProps>;

/**
 * A terminal screen: the whole available box, an explanation, and one way out. For where
 * the app has nothing to show and will get nothing without the user's help — a 404, an
 * unusable share link, an account with no gardens yet.
 *
 * `FullPageLoader` is the transient counterpart. They render the same box and are still two
 * components, because the difference that matters is whether an action is offered — and one
 * component with every prop optional could not require the action this one does.
 *
 * `position: absolute; inset: 0` fills the nearest *positioned* ancestor: the initial
 * containing block for the call sites above `_app` — the 404 in `__root.tsx`, the
 * link-unavailable branch, `Landing` — and `.contentLayer` for the ones that arrive through
 * `<Outlet />`, which is `_app.index.tsx`'s "No gardens yet". One viewport either way, and
 * that is a requirement on `.contentLayer` rather than a coincidence: it is `absolute;
 * inset: 0` partly so this box has something to fill. Flatten it to `position: relative` and
 * its height becomes its in-flow children's — zero, since this box is out of flow — and this
 * screen collapses to a strip at the top of the content area. `docs/scrolling.md` §1 and §10
 * item 10.
 *
 * `order={1}` on the heading is deliberate: no call site has another `h1`. The 404 renders
 * instead of `_app`, the link-unavailable branch returns before `AppChrome`, and
 * `GardenMenu`'s own `h1` appears only once a garden resolves.
 */
export const FullPageMessage = factory<FullPageMessageFactory>((_props) => {
  const props = useProps("FullPageMessage", defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    attributes,
    hero,
    title,
    body,
    action,
    ...others
  } = props;

  const getStyles = useStyles<FullPageMessageFactory>({
    name: "FullPageMessage",
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    vars,
    attributes,
  });

  return (
    <Box {...getStyles("root")} {...others}>
      {hero && <Box {...getStyles("hero")}>{hero}</Box>}
      <Title order={1} {...getStyles("title")}>
        {title}
      </Title>
      <Text {...getStyles("body")}>{body}</Text>
      <Box {...getStyles("action")}>{action}</Box>
    </Box>
  );
});

FullPageMessage.classes = classes;
FullPageMessage.displayName = "FullPageMessage";
